export interface ResponseBody<T = any> {
  message: string;
  code: number;
  data?: T | T[];
}

/** 统一返回结构体 */

export interface PageResult<T = any> {
  data: T[];
  current?: number;
  pageSize?: number;
  total?: number;
  success: boolean;
}

export interface RequestResult<T = any> {
  data: T;
  success: boolean;
  errorMessage: string;
}

export const ConditionOperatorMap = {
  lt: '小于', // 小于
  lte: '小于等于', // 小于等于
  gt: '大于', // 大于
  gte: '大于等于', // 大于等于
  between: '介于', // 介于
  notbetween: '不介于', // 不介于
  in: '包含', // 包含,针对多个值
  eq: '等于', // 等于
  not_eq: '不等于', // 不等于
  contains: '包含（大小写）',
  icontains: '包含',
  startswith: '开头（大小写）',
  istartswith: '开头',
  endswith: '结尾（大小写）',
  iendswith: '结尾',
  notin: '不包含', // 不包含,针对多个值
  notcontains: '不包含(大小写)', // 不包含,针对字符串区分大小写
  noticontains: '不包含', // 不包含,针对字符串不区分大小写
  notstartswith: '不以...开头', // 不包含,针对字符串区分大小写
  notistartswith: '不以...开头，不区分大小写', // 不包含,针对字符串不区分大小写
  notendswith: '不以...结尾（大小写）',
  notiendswith: '不以...结尾',
  _eq: '选择', // 特殊日期
  is_or_not_blank_or_null: '是否为空字符串',
  is_or_not_null: '是否为空',
} as const;
export const operator_params_count = {
  between: 2,
  gt: 1,
  lt: 1,
  gte: 1,
  lte: 1,
  in: 0,
  eq: 1,
  _eq: 1,
  contains: 1,
  icontains: 1,
  startswith: 1,
  notbetween: 2,
  notin: 0,
  notcontains: 1,
  not_eq: 1,
  not_blank_not_null: 1,
  json_list_not_blank_not_null: 1,
  json_list_is_blank_is_null: 1,
  is_or_not_blank_or_null: 1,
  is_or_not_null: 1,

  // ---- 以下为补齐的键 ----
  istartswith: 1, // 对应 'istartswith'
  endswith: 1, // 对应 'endswith'
  iendswith: 1, // 对应 'iendswith'
  noticontains: 1, // 对应 'noticontains'
  notstartswith: 1, // 对应 'notstartswith'
  notistartswith: 1, // 对应 'notistartswith'
  notendswith: 1, // 对应 'notendswith'
  notiendswith: 1, // 对应 'notiendswith'
};
export type ConditionOperator = keyof typeof ConditionOperatorMap;

export const BtnTypeMap = {
  dropdown: 'dropdown', // 下拉
} as const;
type BtnType = keyof typeof BtnTypeMap;

export const ModelKindMap = {
  confirm: '确认提示框', // （定义显示的title 和 content，和ok 和 cancel 分别调用的接口）
  form: '通用表单', // 带有操作组件的表单
  custom_component: '自定义组件', // 自定义组件
} as const;
type ModelKind = keyof typeof ModelKindMap;

export interface ConditionConfig {
  flag: string; //字段
  type: string; // 字段类型
  operator: ConditionOperator; // 操作符
  value: any[]; // 值
}

export interface ConfirmModelConfig {
  title: string; // 标题
  content: string; // 内容
  ok_url?: string | null; // 确定按钮回调
  cancel_url?: string | null; // 取消按钮回调
  method?: 'post' | 'get' | 'put' | 'delete' | null; // 请求方法,默认post
  init_data?: Record<string, any>; // 初始化数据
  api_fields?: Record<string, string>; // key为提交字段，value为数据来源字段, head_btn 无效
  batch_api_fields?: Record<string, string>; // 批量操作时, 从dataSource中获取数据
}
export interface ComponentModelConfig {
  title?: string | null; // 标题
  component: string; // 组件名称
  ok_url?: string | null; // 确定按钮回调
  cancel_url?: string | null; // 取消按钮回调
  method?: 'post' | 'get' | 'put' | 'delete' | null; // 请求方法,默认post
  init_data?: Record<string, any>; // 初始化数据
  api_fields?: Record<string, string>; // key为提交字段，value为数据来源字段, head_btn 无效
  batch_api_fields?: Record<string, string>; // 批量操作时, 从dataSource中获取数据
}

export interface FormConfig {
  settings: Object;
  dynamicFieldList: Object[];
  model: Object;
  rules: Object;
  options: Object;
}
export interface FormModelConfig {
  title: string; // 标题
  form: FormConfig;
  ok_url: string | null; // 确定按钮回调
  cancel_url: string | null; // 取消按钮回调
  method?: 'post' | 'get' | 'put' | 'delete' | null; // 请求方法,默认post
  init_data?: Record<string, any>; // 初始化数据
  api_fields?: Record<string, string>; // key为提交字段，value为数据来源字段
  batch_api_fields?: Record<string, string>; // 批量操作时, 从dataSource中获取数据
}

type BtnConfigBase = {
  name: string; // 按钮名称
  type?: BtnType | null; // 按钮类型
  authority?: string | null; // 按钮权限
  order?: number | null; // 按钮显示顺序
  clazz?: string | null; // 按钮样式
  children?: BtnConfigBase[]; // 子按钮
  show_condition?: ConditionConfig[][] | null; // 按钮显示条件
  disabled_condition?: ConditionConfig[][] | null; // 按钮禁用条件
  urls?: string[] | null; // 使用cb 时所需的urls，用于权限配置
} & (
  | {
      model_kind?: ModelKind; // 模态框类型
      model_config?: ConfirmModelConfig | ComponentModelConfig | FormModelConfig; // 模态框配置
      cb?: never | null;
    }
  | {
      cb?: (Object) => void; // 按钮点击事件，特殊情况下使用,如果定义了model_kind,model_config，则该配置无效
      model_kind?: never | null;
      model_config?: never | null;
    }
);

export type BtnConfig = BtnConfigBase & {
  is_show?: Boolean | (({ record }: { record: any }) => boolean) | null; // 按钮是否显示, 配置了show_condition后，is_show无效
  is_disabled?: Boolean | (({ record }: { record: any }) => boolean) | null; // 按钮是否禁用
  preProcessData?: ({ record }: { record: any }) => any; // 数据预处理函数
  beforeCommitProcessData?: ({ record, data }: { record: any; data: any }) => any; // 数据提交前预处理函数
  children?: BtnConfig[];
};
export type HeadBtnConfig = BtnConfigBase & {
  is_show?:
    | Boolean
    | (({ dataSource, selectedRowKeys }: { dataSource: any[]; selectedRowKeys: any[] }) => boolean)
    | null; // 按钮是否显示
  is_disabled?:
    | Boolean
    | (({ dataSource, selectedRowKeys }: { dataSource: any[]; selectedRowKeys: any[] }) => boolean)
    | null; // 按钮是否禁用
  preProcessData?: ({
    dataSource,
    selectedRowKeys,
  }: {
    dataSource: any[];
    selectedRowKeys: any[];
  }) => any[]; // 数据预处理函数
  beforeCommitProcessData?: ({
    dataSource,
    selectedRowKeys,
    data,
  }: {
    dataSource: any[];
    selectedRowKeys: any[];
    data: any;
  }) => any[]; // 数据提交前预处理函数
  children?: HeadBtnConfig[];
};

export type BtnListConfig = BtnConfig[];
export type HeadBtnListConfig = HeadBtnConfig[];

export type TitleListSubConfig = {
  show_condition?: ConditionConfig[][] | null; // 按钮显示条件
  disabled_condition?: ConditionConfig[][] | null; // 按钮禁用条件
  is_show?: Boolean | (({ record }: { record: any }) => boolean) | null; // 按钮是否显示, 配置了show_condition后，is_show无效
  is_disabled?: Boolean | (({ record }: { record: any }) => boolean) | null; // 按钮是否禁用
};
export type TitleListCreateSubConfig = {
  show_condition?: ConditionConfig[][] | null; // 按钮显示条件
  disabled_condition?: ConditionConfig[][] | null; // 按钮禁用条件
  is_show?:
    | Boolean
    | (({ dataSource, selectedRowKeys }: { dataSource: any[]; selectedRowKeys: any[] }) => boolean)
    | null; // 按钮是否显示
  is_disabled?:
    | Boolean
    | (({ dataSource, selectedRowKeys }: { dataSource: any[]; selectedRowKeys: any[] }) => boolean)
    | null; // 按钮是否禁用
};
export type TitleListConfig = {
  title: string; // 标题
  addModalTitle: string; // 新建按钮及模态框标题
  updateTitle: string; // 修改模态框标题
  is_create: boolean | string; // 创建按钮权限
  is_update: boolean | string; // 修改按钮权限
  is_remove: boolean | string; // 删除按钮权限
  is_recover: boolean | string; // 恢复按钮权限
  create_config?: TitleListCreateSubConfig;
  update_config?: TitleListSubConfig;
  remove_config?: TitleListSubConfig;
  recover_config?: TitleListSubConfig;
};
